import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchFoundersForLogin } from '../api/client';

export default function LoginPage() {
  const { login } = useAuth();
  const [founders, setFounders] = useState<{ id: string; name: string }[]>([]);
  const [personId, setPersonId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [foundersError, setFoundersError] = useState('');
  const [phase, setPhase] = useState<'logo' | 'form'>('logo');

  useEffect(() => {
    fetchFoundersForLogin()
      .then(setFounders)
      .catch(() => setFoundersError('Failed to load people. Try refreshing.'));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setPhase('form'), 2500);
    return () => clearTimeout(t);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(password, personId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  }

  const submitDisabled = loading || !personId || !password;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--color-navy-deep)',
      }}
    >
      {phase === 'logo' && (
        <img
          src="/pnglogonotext.png"
          alt="Jambo Labs"
          className="login-hero-logo"
        />
      )}
      {phase === 'form' && (
      <form
        onSubmit={handleSubmit}
        className="card card-padded login-card-enter"
        style={{
          width: '360px',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <img
          src="/pnglogonotext.png"
          alt="Jambo Labs"
          className="login-card-logo"
        />
        <h1
          style={{
            margin: '0 0 24px',
            fontSize: '26px',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            textAlign: 'center',
            letterSpacing: '-0.01em',
          }}
        >
          Jambo Jira
        </h1>

        {/* Hidden username field for accessibility (single-user app; password-only auth) */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
        />

        <label
          htmlFor="login-person"
          style={{
            display: 'block',
            marginBottom: '6px',
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          Who are you?
        </label>
        <select
          id="login-person"
          className="select"
          value={personId}
          onChange={(e) => setPersonId(e.target.value)}
          disabled={loading || founders.length === 0}
          style={{ marginBottom: '16px' }}
        >
          <option value="" disabled>
            {foundersError ? foundersError : founders.length === 0 ? 'Loading…' : 'Select your name…'}
          </option>
          {founders.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>

        <label
          htmlFor="login-password"
          style={{
            display: 'block',
            marginBottom: '6px',
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          Password
        </label>
        <input
          id="login-password"
          className="input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          autoFocus
          disabled={loading}
          style={{
            marginBottom: '16px',
            borderColor: error ? 'var(--color-coral)' : undefined,
          }}
        />

        {error && (
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--color-coral)' }}>{error}</p>
        )}

        <button
          type="submit"
          disabled={submitDisabled}
          className="btn btn-primary"
          style={{ width: '100%' }}
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      )}
    </div>
  );
}
